import { configuredCategories, emptyPreferences } from './consent'
import { modalStyles } from './styles'
import { modalColorTokens, type ColorMode, type ConsentAction, type ConsentCategory, type ConsentPreferences, type ConsentState, type InitOptions, type ModalDisplayMode, type ModalPalette, type RuntimeOptions, type SettingsButtonPosition, type PublicConfiguration, type ModalTheme } from './types'

type DecisionHandler = (action: ConsentAction, preferences: ConsentPreferences, language: string) => Promise<ConsentState>
type ModalTab = 'consent' | 'details' | 'about'

export class ConsentModal {
  private readonly host = document.createElement('cmp-consent-root')
  private readonly shadow = this.host.attachShadow({ mode: 'open' })
  private readonly overlay = document.createElement('div')
  private readonly dialog = document.createElement('section')
  private readonly settingsButton = document.createElement('button')
  private readonly announcer = document.createElement('p')
  private readonly mediaQuery = matchMedia('(prefers-color-scheme: dark)')
  private readonly compactQuery = matchMedia('(max-width: 640px)')
  private readonly expandedCategories = new Map<ConsentCategory, boolean>()
  private displayMode: ModalDisplayMode
  private settingsPosition: SettingsButtonPosition
  private colorMode: ColorMode
  private theme: ModalTheme
  private runtimeOptions: RuntimeOptions
  private tab: ModalTab = 'consent'
  private language: string
  private preferences = emptyPreferences()
  private savedState: ConsentState | null = null
  private previousFocus: HTMLElement | null = null
  private busy = false
  private destroyed = false

  private get panelDisplayMode() {
    return this.displayMode === 'auto-accept' ? 'banner' : this.displayMode
  }

  constructor(
    private readonly configuration: PublicConfiguration,
    private readonly options: InitOptions,
    private readonly onDecision: DecisionHandler,
    private readonly onError?: (error: unknown) => void,
  ) {
    this.displayMode = options.displayMode ?? configuration.project.policy.modalPresentation?.displayMode ?? 'modal'
    this.settingsPosition = resolveSettingsPosition(configuration.project.policy.modalPresentation?.settingsButtonPosition, options)
    this.colorMode = options.colorMode ?? 'auto'
    this.theme = options.theme ?? configuration.project.policy.modalTheme
    this.runtimeOptions = {
      language: options.language,
      colorMode: options.colorMode,
      theme: options.theme,
      displayMode: options.displayMode,
      settingsButton: options.settingsButton,
      settingsPosition: options.settingsPosition,
    }
    this.language = chooseLanguage(configuration, options.language)
    this.setup()
  }

  mount() {
    document.body.append(this.host)
    this.applyTheme()
    this.mediaQuery.addEventListener('change', this.handleColorChange)
    this.compactQuery.addEventListener('change', this.handleLayoutChange)
    return this
  }

  setSavedState(state: ConsentState) {
    this.busy = false
    this.savedState = state
    this.preferences = { ...state.preferences }
    this.language = state.language
    this.close()
  }

  openInitial() {
    if (this.destroyed) return
    this.tab = 'consent'
    this.expandedCategories.clear()
    this.preferences = emptyPreferences()
    this.open()
  }

  openPreferences() {
    if (this.destroyed) return
    this.tab = 'details'
    this.expandedCategories.clear()
    this.preferences = this.savedState ? { ...this.savedState.preferences } : emptyPreferences()
    this.open()
  }

  updateOptions(next: RuntimeOptions) {
    this.runtimeOptions = { ...this.runtimeOptions, ...next }
    if (next.language !== undefined) this.language = chooseLanguage(this.configuration, next.language)
    if (next.colorMode !== undefined) this.colorMode = next.colorMode
    if (next.theme !== undefined) this.theme = structuredClone(next.theme)
    if (next.displayMode !== undefined) this.displayMode = next.displayMode
    if (next.settingsButton !== undefined || next.settingsPosition !== undefined) {
      this.settingsPosition = resolveSettingsPosition(
        this.configuration.project.policy.modalPresentation?.settingsButtonPosition,
        { ...this.options, ...this.runtimeOptions },
      )
    }

    this.applyTheme()
    this.overlay.dataset.mode = this.panelDisplayMode
    this.dialog.setAttribute('aria-modal', String(this.displayMode === 'modal'))
    this.settingsButton.dataset.position = this.settingsPosition
    if (this.overlay.dataset.open === 'true') this.render()
    else this.settingsButton.dataset.visible = this.savedState && this.settingsPosition !== 'none' ? 'true' : 'false'
  }

  destroy() {
    if (this.destroyed) return
    const restoreFocus = this.overlay.dataset.open === 'true'
    this.destroyed = true
    this.busy = false
    this.overlay.dataset.open = 'false'
    this.host.remove()
    try {
      this.mediaQuery.removeEventListener('change', this.handleColorChange)
      this.compactQuery.removeEventListener('change', this.handleLayoutChange)
    } finally {
      if (restoreFocus) this.previousFocus?.focus()
    }
  }

  private handleError(error: unknown) {
    try { this.destroy() } catch (cleanupError) { console.error('[CMP] Unable to clean up consent UI:', cleanupError) }
    if (this.onError) this.onError(error)
    else console.error('[CMP] Unable to display consent management:', error)
  }

  private runSafely(operation: () => void) {
    if (this.destroyed) return
    try { operation() } catch (error) { this.handleError(error) }
  }

  private listen<K extends keyof HTMLElementEventMap>(
    target: HTMLElement,
    event: K,
    listener: (event: HTMLElementEventMap[K]) => void,
  ) {
    target.addEventListener(event, (value) => this.runSafely(() => listener(value)))
  }

  private setup() {
    const style = document.createElement('style')
    style.textContent = modalStyles
    this.shadow.append(style)

    this.overlay.className = 'cmp-overlay'
    this.overlay.dataset.open = 'false'
    this.overlay.dataset.mode = this.panelDisplayMode
    this.listen(this.overlay, 'click', (event) => {
      if (this.displayMode === 'modal' && event.target === this.overlay && this.savedState && !this.busy) this.close()
    })

    this.dialog.className = 'cmp-dialog'
    this.dialog.setAttribute('role', 'dialog')
    this.dialog.setAttribute('aria-modal', String(this.displayMode === 'modal'))
    this.dialog.tabIndex = -1
    this.listen(this.dialog, 'keydown', (event) => this.handleKeydown(event))
    this.overlay.append(this.dialog)

    this.settingsButton.className = 'cmp-settings'
    this.settingsButton.type = 'button'
    this.settingsButton.dataset.visible = 'false'
    this.settingsButton.dataset.position = this.settingsPosition
    this.settingsButton.setAttribute('aria-label', this.t('consentModal.buttons.managePreferences'))
    this.settingsButton.append(cookieIcon())
    this.listen(this.settingsButton, 'click', () => this.openPreferences())
    this.announcer.className = 'cmp-visually-hidden'
    this.announcer.setAttribute('aria-live', 'polite')
    this.announcer.setAttribute('aria-atomic', 'true')
    this.shadow.append(this.overlay, this.settingsButton, this.announcer)
  }

  private open() {
    this.previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null
    this.render()
    this.overlay.dataset.open = 'true'
    this.settingsButton.dataset.visible = 'false'
    if (this.displayMode === 'modal') queueMicrotask(() => this.runSafely(() => {
      if (this.host.isConnected && this.overlay.dataset.open === 'true') this.dialog.focus()
    }))
  }

  private close(restoreFocus = true) {
    this.overlay.dataset.open = 'false'
    this.overlay.dataset.mode = this.panelDisplayMode
    if (this.savedState && this.settingsPosition !== 'none') this.settingsButton.dataset.visible = 'true'
    if (restoreFocus) this.previousFocus?.focus()
  }

  private render() {
    this.dialog.replaceChildren()
    const headingId = `cmp-title-${this.configuration.project.publicKey}`
    this.dialog.setAttribute('aria-labelledby', headingId)
    this.dialog.setAttribute('aria-busy', String(this.busy))
    this.dialog.dataset.tab = this.tab
    if (this.tab === 'consent') this.dialog.setAttribute('aria-describedby', 'cmp-notice')
    else this.dialog.removeAttribute('aria-describedby')

    const header = element('header', 'cmp-header')
    const title = element('h2', 'cmp-title', this.t('consentModal.title'))
    title.id = headingId
    header.append(title)
    if (this.configuration.project.policy.availableLanguages.length > 1) header.append(this.languageSelector())
    if (this.savedState) {
      const close = element('button', 'cmp-close', '×')
      close.type = 'button'
      close.disabled = this.busy
      close.setAttribute('aria-label', this.t('consentModal.buttons.close'))
      this.listen(close, 'click', () => this.close())
      header.append(close)
    }

    const body = element('div', 'cmp-body')
    body.append(this.tabPanel())

    this.dialog.append(header)
    if (this.tab !== 'consent') this.dialog.append(this.tabs())
    this.dialog.append(body, this.footer())
  }

  private languageSelector() {
    const label = element('label')
    const labelText = element('span', 'cmp-visually-hidden', this.t('consentModal.labels.language'))
    const select = element('select', 'cmp-language')
    select.setAttribute('aria-label', this.t('consentModal.labels.language'))
    for (const language of this.configuration.project.policy.availableLanguages) {
      const option = document.createElement('option')
      option.value = language
      option.textContent = language.toUpperCase()
      option.selected = language === this.language
      select.append(option)
    }
    this.listen(select, 'change', () => {
      this.language = select.value
      this.render()
      this.dialog.focus()
    })
    label.append(labelText, select)
    return label
  }

  private tabs() {
    const tabs = element('div', 'cmp-tabs')
    tabs.setAttribute('role', 'tablist')
    tabs.setAttribute('aria-label', this.t('consentModal.title'))
    const entries: Array<[ModalTab, string]> = [
      ['consent', this.t('consentModal.tabs.consent')],
      ['details', this.t('consentModal.tabs.details')],
      ['about', this.t('consentModal.tabs.about')],
    ]
    entries.forEach(([tab, label], index) => {
      const button = element('button', 'cmp-tab', label)
      button.type = 'button'
      button.id = `cmp-tab-${tab}`
      button.setAttribute('role', 'tab')
      button.setAttribute('aria-selected', String(this.tab === tab))
      button.setAttribute('aria-controls', `cmp-panel-${tab}`)
      button.tabIndex = this.tab === tab ? 0 : -1
      this.listen(button, 'click', () => this.selectTab(tab))
      this.listen(button, 'keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return
        event.preventDefault()
        const buttons = [...tabs.querySelectorAll<HTMLButtonElement>('[role="tab"]')]
        const nextIndex = event.key === 'Home' ? 0
          : event.key === 'End' ? buttons.length - 1
          : (index + (event.key === 'ArrowRight' ? 1 : -1) + buttons.length) % buttons.length
        buttons[nextIndex]?.focus()
        buttons[nextIndex]?.click()
      })
      tabs.append(button)
    })
    return tabs
  }

  private selectTab(tab: ModalTab) {
    this.tab = tab
    this.render()
    const target = this.shadow.querySelector<HTMLElement>(`#cmp-tab-${tab}`) ?? this.dialog
    target.focus()
  }

  private tabPanel() {
    const panel = element('div', 'cmp-panel')
    panel.id = `cmp-panel-${this.tab}`
    if (this.tab !== 'consent') {
      panel.setAttribute('role', 'tabpanel')
      panel.setAttribute('aria-labelledby', `cmp-tab-${this.tab}`)
    }

    if (this.tab === 'details') {
      panel.append(this.categoryList())
      return panel
    }

    if (this.tab === 'about') {
      panel.append(...this.aboutContent())
      return panel
    }

    const notice = element('p', 'cmp-description', this.t('consentModal.description'))
    notice.id = 'cmp-notice'
    panel.append(notice)
    if (!this.compactQuery.matches) {
      const information = element('div', 'cmp-information')
      information.append(this.aboutButton())
      const privacyUrl = safePrivacyPolicyUrl(this.configuration.project.policy.privacyPolicyUrl)
      if (privacyUrl) {
        const policy = element('a', 'cmp-link', this.t('consentModal.links.privacyPolicy'))
        policy.href = privacyUrl
        policy.target = '_blank'
        policy.rel = 'noopener noreferrer'
        information.append(policy)
      }
      panel.append(information)
    }
    return panel
  }

  private aboutButton() {
    const about = element('button', 'cmp-text-button', this.t('consentModal.tabs.about'))
    about.id = 'cmp-about'
    about.type = 'button'
    about.disabled = this.busy
    this.listen(about, 'click', () => this.selectTab('about'))
    return about
  }

  private aboutContent() {
    const about = element('p', 'cmp-description', this.t('consentModal.about.description'))
    const privacyNotice = element('p', 'cmp-description')
    const privacyUrl = safePrivacyPolicyUrl(this.configuration.project.policy.privacyPolicyUrl)
    const privacyTemplate = this.t('consentModal.about.privacyPolicy')
    const privacyMarker = '{{privacyPolicy}}'
    const policy = element(privacyUrl ? 'a' : 'span', privacyUrl ? 'cmp-link' : undefined, this.t('consentModal.links.privacyPolicy'))
    if (policy instanceof HTMLAnchorElement) {
      policy.href = privacyUrl!
      policy.target = '_blank'
      policy.rel = 'noopener noreferrer'
    }
    const markerIndex = privacyTemplate.indexOf(privacyMarker)
    if (markerIndex < 0) {
      privacyNotice.textContent = privacyTemplate
    } else {
      privacyNotice.append(
        document.createTextNode(privacyTemplate.slice(0, markerIndex)),
        policy,
        document.createTextNode(privacyTemplate.slice(markerIndex + privacyMarker.length)),
      )
    }
    return [about, privacyNotice]
  }

  private categoryList() {
    const list = element('div', 'cmp-categories')
    for (const category of configuredCategories(this.configuration)) {
      const row = element('div', 'cmp-category')
      const selection = element('label', 'cmp-category-select')
      const checkbox = element('input', 'cmp-checkbox')
      checkbox.type = 'checkbox'
      checkbox.id = `cmp-selection-${category}`
      checkbox.checked = category === 'security' ? true : this.preferences[category]
      checkbox.disabled = category === 'security'
      const copy = element('details', 'cmp-category-copy')
      copy.open = this.expandedCategories.get(category) ?? !this.compactQuery.matches
      const summary = element('summary', 'cmp-disclosure-summary')
      summary.id = `cmp-category-summary-${category}`
      const title = element('span', 'cmp-category-title', this.t(`consentModal.sections.${category}.title`))
      title.id = `cmp-category-${category}`
      const description = element('p', 'cmp-category-description', this.t(`consentModal.sections.${category}.description`))
      description.id = `cmp-category-description-${category}`
      checkbox.setAttribute('aria-labelledby', title.id)
      checkbox.setAttribute('aria-describedby', description.id)
      summary.append(title)
      if (category === 'security') summary.append(element('span', 'cmp-always', this.t('consentModal.labels.alwaysActive')))
      copy.append(summary, description)
      this.listen(copy, 'toggle', () => {
        if (copy.isConnected) this.expandedCategories.set(category, copy.open)
      })
      selection.append(checkbox)
      row.append(copy, selection)

      if (category !== 'security') this.listen(checkbox, 'change', () => { this.preferences[category] = checkbox.checked })
      list.append(row)
    }
    return list
  }

  private footer() {
    const footer = element('footer', 'cmp-footer')
    const actions = element('div', 'cmp-actions')
    if (this.tab === 'details') {
      actions.append(
        this.button(this.t('consentModal.buttons.acceptAll'), 'primary', () => void this.decide('accept_all', allPreferences())),
        this.button(this.t('consentModal.buttons.rejectAll'), 'primary', () => void this.decide('reject_all', emptyPreferences())),
        this.button(this.t('consentModal.buttons.savePreferences'), 'secondary', () => void this.decide('save_preferences', this.preferences)),
      )
    } else {
      actions.append(
        this.button(this.t('consentModal.buttons.acceptAll'), 'primary', () => void this.decide('accept_all', allPreferences())),
        this.button(this.t('consentModal.buttons.rejectAll'), 'primary', () => void this.decide('reject_all', emptyPreferences())),
        this.button(this.t('consentModal.buttons.managePreferences'), 'secondary', () => this.selectTab('details')),
      )
    }
    const secondary = actions.lastElementChild as HTMLButtonElement
    secondary.id = 'cmp-secondary-action'
    footer.append(actions)
    return footer
  }

  private button(label: string, variant: 'primary' | 'secondary', onClick: () => void) {
    const button = element('button', `cmp-button cmp-button-${variant}`, label)
    button.type = 'button'
    button.disabled = this.busy
    this.listen(button, 'click', onClick)
    return button
  }

  private async decide(action: ConsentAction, preferences: ConsentPreferences) {
    if (this.busy || this.destroyed) return
    try {
      this.busy = true
      this.render()
      const state = await this.onDecision(action, { ...preferences }, this.language)
      if (this.destroyed) return
      this.setSavedState(state)
      this.announcer.textContent = 'Privacy preferences saved.'
    } catch (error) {
      this.handleError(error)
    }
  }

  private handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      event.preventDefault()
      if (this.savedState && !this.busy) this.close()
      return
    }
    if (event.key !== 'Tab' || this.displayMode !== 'modal') return
    const focusable = [...this.dialog.querySelectorAll<HTMLElement>('button:not(:disabled):not([tabindex="-1"]), input:not(:disabled), select, a[href], summary')]
      .filter((element) => {
        const collapsed = element.closest('details:not([open])')
        return !collapsed || collapsed.querySelector('summary') === element
      })
    if (!focusable.length) return
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    const activeElement = this.shadow.activeElement
    if (event.shiftKey && (activeElement === first || activeElement === this.dialog)) { event.preventDefault(); last?.focus() }
    else if (!event.shiftKey && (activeElement === last || activeElement === this.dialog)) { event.preventDefault(); first?.focus() }
  }

  private applyTheme() {
    const mode = this.colorMode === 'light' || this.colorMode === 'dark'
      ? this.colorMode
      : this.mediaQuery.matches ? 'dark' : 'light'
    setPalette(this.host, this.theme[mode])
  }

  private readonly handleColorChange = () => {
    this.runSafely(() => { if (this.colorMode === 'auto') this.applyTheme() })
  }

  private readonly handleLayoutChange = () => {
    this.runSafely(() => {
      this.expandedCategories.clear()
      if (this.overlay.dataset.open === 'true' && this.tab !== 'about') {
        const focused = this.shadow.activeElement
        this.render()
        if (focused) (this.shadow.getElementById(focused.id) ?? this.dialog).focus()
      }
    })
  }

  private t(key: string) {
    return this.configuration.project.policy.translations[this.language]?.[key] ?? key
  }

}

function resolveSettingsPosition(
  configured: SettingsButtonPosition | undefined,
  options: InitOptions,
): SettingsButtonPosition {
  if (options.settingsButton === false) return 'none'
  if (options.settingsPosition) return options.settingsPosition
  if (options.settingsButton === true && configured === 'none') return 'bottom-right'
  return configured ?? 'bottom-right'
}

function cookieIcon() {
  const namespace = 'http://www.w3.org/2000/svg'
  const svg = document.createElementNS(namespace, 'svg')
  svg.setAttribute('class', 'cmp-settings-icon')
  svg.setAttribute('viewBox', '0 0 24 24')
  svg.setAttribute('aria-hidden', 'true')

  const outline = document.createElementNS(namespace, 'path')
  outline.setAttribute('d', 'M20.8 13.1a4.5 4.5 0 0 1-5.9-5.9A4.5 4.5 0 0 1 10.9 3a9 9 0 1 0 9.9 10.1Z')
  outline.setAttribute('fill', 'none')
  outline.setAttribute('stroke', 'currentColor')
  outline.setAttribute('stroke-width', '1.8')
  outline.setAttribute('stroke-linecap', 'round')
  outline.setAttribute('stroke-linejoin', 'round')
  svg.append(outline)

  for (const [cx, cy] of [['8.2', '9'], ['12.2', '13.2'], ['8.4', '16']]) {
    const crumb = document.createElementNS(namespace, 'circle')
    crumb.setAttribute('cx', cx)
    crumb.setAttribute('cy', cy)
    crumb.setAttribute('r', '1.15')
    crumb.setAttribute('fill', 'currentColor')
    svg.append(crumb)
  }
  return svg
}
export function chooseLanguage(configuration: PublicConfiguration, requested?: string) {
  const available = configuration.project.policy.availableLanguages
  const candidates = [requested, document.documentElement.lang, ...navigator.languages]
    .filter(Boolean)
    .map((language) => language!.toLowerCase().split('-')[0])
  return candidates.find((language) => available.includes(language)) ?? available[0] ?? 'en'
}

export function setPalette(host: HTMLElement, palette: ModalPalette) {
  for (const token of modalColorTokens) {
    host.style.setProperty(`--cmp-${token.replace(/[A-Z]/g, (letter) => '-' + letter.toLowerCase())}`, palette[token])
  }
  host.style.setProperty('--cmp-surface', `color-mix(in srgb, ${palette.background} 94%, ${palette.textSecondary})`)
  host.style.setProperty('--cmp-text', palette.textPrimary)
  host.style.setProperty('--cmp-muted-text', palette.textSecondary)
  host.style.setProperty('--cmp-border', `color-mix(in srgb, ${palette.textSecondary} 42%, transparent)`)
  host.style.setProperty('--cmp-overlay', 'rgb(0 0 0 / 55%)')
  host.style.setProperty('--cmp-primary-text', palette.background)
  host.style.setProperty('--cmp-secondary-text', palette.textPrimary)
  host.style.setProperty('--cmp-focus-ring', palette.primary)
  host.style.setProperty('--cmp-toggle-on', palette.primary)
  host.style.setProperty('--cmp-toggle-off', '#9ca3af')
  host.style.setProperty('--cmp-danger', palette.textSecondary)
  host.style.setProperty('--cmp-danger-text', palette.background)
}

function element<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
  extraClass?: string,
) {
  const node = document.createElement(tag)
  if (className) node.className = className
  if (extraClass) node.classList.add(extraClass)
  if (text !== undefined) node.textContent = text
  return node
}

function allPreferences(): ConsentPreferences {
  return { ads: true, analytics: true, functionality: true, personalization: true }
}

function safePrivacyPolicyUrl(value: string | null) {
  if (!value) return null
  if (value.startsWith('#')) return value
  try {
    const protocol = new URL(value).protocol.toLowerCase()
    return protocol === 'http:' || protocol === 'https:' ? value : null
  } catch {
    return null
  }
}
